import { ChangeEvent, useCallback, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { parsePhoneNumberFromString } from "libphonenumber-js/mobile";
import { Button } from "./button";
import WhatsAppIcon from "./whatsappicon";

const countryCodes = [
  { code: "+52", country: "MX", flag: "🇲🇽" },
  { code: "+1", country: "US", flag: "🇺🇸" },
  // Add other country codes...
];

type WhatsappInputProps = {
  onSubmit: (fullNumber: string) => Promise<void>;
  isLoading?: boolean;
};

export default function WhatsappInput({
  onSubmit,
  isLoading = false,
}: WhatsappInputProps) {
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Validate phone number as user types
  const validatePhoneNumber = useCallback(
    (input: string) => {
      const fullNumber = `${selectedCountry.code}${input}`;
      const phoneNumberObj = parsePhoneNumberFromString(fullNumber);
      return phoneNumberObj?.isValid() || false;
    },
    [selectedCountry.code]
  );

  const handlePhoneNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/[^\d]/g, ""); // Only allow digits
    setPhoneNumber(input);
    setIsValid(validatePhoneNumber(input));
    setError(null);
  };

  const handleSubmit = async () => {
    const fullNumber = `${selectedCountry.code}${phoneNumber}`;
    if (!validatePhoneNumber(phoneNumber)) {
      setError("Por favor ingresa un número válido");
      return;
    }
    
    try {
      await onSubmit(fullNumber);
    } catch (error) {
      setError(error instanceof Error ? error.message : "Error al enviar el código");
    }
  };

  return (
    <div className="w-full max-w-sm space-y-4">
      <div className="flex flex-col gap-4">
        <div className="flex">
          <Select
            value={selectedCountry.code}
            onValueChange={(value) =>
              setSelectedCountry(
                countryCodes.find((c) => c.code === value) || countryCodes[0]
              )
            }
            disabled={isLoading}
          >
            <SelectTrigger className="w-[110px] border-r-0 rounded-r-none focus:ring-0 focus:ring-offset-0">
              <SelectValue>
                <span className="flex items-center">
                  <span className="mr-2">{selectedCountry.flag}</span>
                  <span>{selectedCountry.code}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {countryCodes.map((country) => (
                <SelectItem key={country.code} value={country.code}>
                  <span className="flex items-center">
                    <span className="mr-2">{country.flag}</span>
                    <span>{country.code}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="tel"
            placeholder="Tu número de teléfono"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            className={`flex-grow rounded-l-none ${
              !isValid && phoneNumber ? "border-red-500" : ""
            }`}
            disabled={isLoading}
          />
        </div>
        <Button
          onClick={handleSubmit}
          disabled={!isValid || !phoneNumber || isLoading}
          className="w-full"
        >
          <WhatsAppIcon className="h-5 w-5 mr-2" />
          {isLoading ? "Enviando código..." : "Enviar código"}
        </Button>
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}
